<template>
  <header class="header--wrap">
    <div class="header">
      <logo></logo>

      <div class="title--area">
        <transition name="title">
          <p v-if="!title"
             v-html="userInfoSummary"></p>
          <h2 v-else
              v-html="title"
              :key="title"></h2>
        </transition>
      </div>
    </div>
    <div class="button--area clearfix" v-if="userData">
      <battery-indicator
        :description="pvDescription"/>

      <ui-button class="charge-pv"
                 v-if="creditData.percentage <= 30"
                 :disabled="!creditData.canChargePv"
                 :backgroundColor="'#1199af'"
                 :primary4="true"
                 :size="'small'"
                 :iconName="'icon--pv-plug'"
                 @click="onChargePv">
        {{ $t('common.chargePv') }}
      </ui-button>

      <button class="user-menu" @click.stop.prevent="toggleUserMenu">
        <i class="icon icon--user"></i>
      </button>
      <transition name="slide">
        <user-menu v-if="showUserMenu" />
      </transition>
    </div>
  </header>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import UiButton from '@/components/ui/UiButton'
import BatteryIndicator from '@/components/header/BatteryIndicator'
import UserMenu from '@/components/header/UserMenu'
import Logo from '@/components/common/Logo.vue'

export default {
  name: 'DashboardHeader',
  components: {
    Logo,
    UiButton,
    BatteryIndicator,
    UserMenu
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'showUserMenu'
    ]),
    ...mapGetters([
      'creditData'
    ]),
    userInfoSummary () {
      const planData = this.planData
      let summary
      let planName = planData.planName

      if (planName && this.creditData.hasPlan) {
        summary = this.$t('gnb.plan.usingPlan', {
          userName: this.userData.userId,
          planName: planName
        })
      } else {
        summary = this.$t('gnb.plan.usingNoPlan', {
          userName: this.userData.userId
        })
      }
      return summary
    },
    pvDescription () {
      if (!this.creditData.hasPlan) {
        return this.$t('gnb.battery.noUsingPlan')
      } else if (this.creditData.isUnlimited) {
        return this.$t('gnb.battery.noLimit')
      } else if (!this.creditData.remain) {
        return this.$t('gnb.battery.empty')
      } else {
        let remain = this.creditData.remain >= 0 ? this.creditData.remain : 0
        return this.$t('gnb.battery.available', {
          pv: this.$options.filters.thousand(remain)
        })
      }
    }
  },
  data () {
    return {
      title: null
    }
  },
  created () {
    EventBus.$on('setTitle', (value) => {
      this.title = value
    })
  },
  mounted () {
    EventBus.$on('closeUserMenu', () => {
      this.setShowUserMenu(false)
    })
  },
  methods: {
    ...mapMutations([
      'setShowUserMenu',
      'clearAllUserData'
    ]),
    toDashboard () {
      EventBus.$emit('onLoading', true)
      if (this.$route.name !== 'dashboard') {
        setTimeout(() => {
          this.$router.push({ name: 'dashboard' })
        }, 300)
      } else {
        location.href = '/tool'
      }
    },
    toggleUserMenu () {
      EventBus.$emit('closeAllUiSelectBox')
      this.setShowUserMenu(!this.showUserMenu)
      this.showLanguageListStatus = false
    },
    onChargePv () {
      this.$router.push({ name: 'managePlan', params: { action: 'chargePv' } })
    }
  }
}
</script>

<style lang="scss" scoped>
.header--wrap {
  position: relative;
  z-index: 2;
  body.dimmed-open & {
    z-index: 0;
  }
  &.scroll {
    &:after {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 28px;
      background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0) 60%);
    }
  }
  .header {
    position: relative;
    z-index: 1;
    height: 80px;
    padding-left: 0;

    .title--area {
      position: relative;
      vertical-align: top;
      display: inline-block;
      height: 60px;
      border-left: 1px solid #c1c1c1;
      font-size: 13px;
      white-space: nowrap;

      h2 {
        position: absolute;
        top: 0;
        left: 0;
        font-size: 22px;
        color: #444444;
        padding: 25px 0 0 15px;
        font-weight: 500;

        /deep/ {
          .blue {
            color: #289eef;

            em {
              display: inline-block;
              padding: 5px 6px;
              font-size: 12px;
              color: #289eef;
              border-radius: 5px;
              border: solid 1px #289eef;
              background-color: #d1ecff;
              margin-left: 8px;
              transform: translateY(-3px);
            }
          }
        }
      }

      p {
        position: absolute;
        top: 0;
        left: 0;
        padding: 19px 0 0 21px;
      }

      /deep/ {
        .user-name {
          color: #444444;
          font-size: 14px;
        }

        .plan-name {
          color: #ec0047;
        }
      }
    }
  }

  .button--area {
    position: absolute;
    z-index: 1;
    top: 11px;
    right: 15px;
    transform: none;

    button.user-menu {
      width: 30px;
      height: 30px;
      border-radius: 15px;
      display: inline-block;
      vertical-align: middle;
      transition: background-color 0.2s ease;
      margin: 0 10px 0 15px;

      .icon--user {
        margin: 0;
      }

      &:hover {
        background-color: rgba(0, 0, 0, 0.06);
      }
    }

    button.charge-pv {
      margin-top: -2px;
      margin-left: 12px;
      transform: translateY(1px);
    }
  }

  .title-enter-active, .title-leave-active {
    transition: transform 0.5s ease,
                opacity 0.5s ease;
  }
  .title-enter{
    transform: translateY(-50px);
    opacity: 0;
  }
  .title-leave-to{
    transform: translateY(30px);
    opacity: 0;
  }
}
</style>
